import { FC, useState } from 'react'

import { Box, Button, HStack, IconButton, Popover, Portal, Spacer, Text } from '@chakra-ui/react'
import { ResetIcon } from '@editor/assets/icons'
import {
  EDITOR_DEFAULT_LABEL_FONT_SIZE,
  EDITOR_DEFAULT_MESH_TRANSPARENCY,
  EDITOR_DEFAULT_PCD_TRANSPARENCY,
} from '@editor/config/styles'
import { Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import {
  resetBackgroundColor,
  resetPcdTransparency,
  resetPointSize,
  setBackgroundColor,
  setIsPointCloudInvisible,
  setPcdTransparency,
  setPointSize,
} from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { resetTransparency, setIsVisible, setTransparency } from '@editor/pages/projects/common/SolidMeshManager/store'
import LayerItem from '@editor/pages/projects/editor/infoPanels/components/LayerItem'
import { RootState, useAppDispatch } from '@editor/store/app'
import { resetLabelFontSize, setLabelFontSize } from '@editor/store/tool'
import { RangeInput, Tooltip } from '@infraloop/common'
import { Chrome } from '@uiw/react-color'
import { ChromeInputType } from '@uiw/react-color-chrome'
import { useSelector } from 'react-redux'

const PCDSettings: FC = () => {
  // Store
  const dispatch = useAppDispatch()
  const labelFontSize = useSelector((state: RootState) => state.toolUtil.core.labelFontSize)

  const initCompleted = useSelector((state: RootState) => state.blueprint.editor.initCompleted)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const isPointCloudInvisible = useSelector((state: RootState) => state.blueprint.editor.isPointCloudInvisible)
  const pcdTransparency = useSelector((state: RootState) => state.blueprint.editor.pcdTransparency)
  const pointSize = useSelector((state: RootState) => state.blueprint.editor.pointSize)
  const defaultPointSize = useSelector((state: RootState) => state.blueprint.editor.defaultPointSize)
  const backgroundColor = useSelector((state: RootState) => state.blueprint.editor.backgroundColor)

  const isMeshFilePresent = useSelector((state: RootState) => state.solidMesh.isPresent)
  const isMeshFileVisible = useSelector((state: RootState) => state.solidMesh.isVisible)
  const solidMeshTransparency = useSelector((state: RootState) => state.solidMesh.transparency)

  // States
  const [isManualEditingPointSize, setIsManualEditingPointSize] = useState(false)
  const [isManualEditingPcdTransparency, setIsManualEditingPcdTransparency] = useState(false)
  const [isManualEditingLabelFontSize, setIsManualEditingLabelFontSize] = useState(false)
  const [isManualEditingMeshOpacity, setIsManualEditingMeshOpacity] = useState(false)

  // Derived states
  const meshUnavailableMessage = 'エリア設定からメッシュファイルをアップロードしてください'

  if (!initCompleted) return null

  return (
    <>
      {/* Point Cloud Controls Group */}
      <Text fontWeight="bold" fontSize="sm" color="gray2.500" mt={2} mb={1}>
        点群
      </Text>

      <Box pl={2}>
        {/* Point size */}
        <HStack gap={0} w="100%">
          {!isManualEditingPointSize && (
            <Text flex={1} whiteSpace="nowrap">
              点のサイズ
            </Text>
          )}
          {pointSize !== undefined && (
            <RangeInput
              colorPalette="black"
              size="sm"
              key="pointSize"
              testId="pointSize"
              min={0.1}
              max={10}
              step={0.1}
              precision={1}
              onManualEdit={setIsManualEditingPointSize}
              value={pointSize}
              defaultValue={defaultPointSize}
              displayValue={`${pointSize}`}
              updateValue={(newValue) => {
                if (newValue) {
                  dispatch(setPointSize(newValue))
                }
              }}
              onReset={() => {
                setTimeout(() => dispatch(resetPointSize()))
              }}
            />
          )}
        </HStack>

        {/* PCD transparency */}
        <HStack gap={0} w="100%">
          {!isManualEditingPcdTransparency && <Text mr={2}>不透明度</Text>}
          <Spacer />
          {pcdTransparency !== undefined && (
            <RangeInput
              colorPalette="black"
              size="sm"
              key="pcdTransparency"
              testId="pcdTransparency"
              min={0}
              max={100}
              step={10}
              precision={0}
              value={pcdTransparency * 100}
              defaultValue={EDITOR_DEFAULT_PCD_TRANSPARENCY * 100}
              displayValue={`${pcdTransparency * 100}`}
              unit="%"
              onManualEdit={setIsManualEditingPcdTransparency}
              updateValue={(newValue) => {
                dispatch(setPcdTransparency(newValue / 100))
              }}
              onReset={() => {
                setTimeout(() => dispatch(resetPcdTransparency()))
              }}
            />
          )}
        </HStack>

        {/* Background color - hidden in 2D mode since it's fixed to white */}
        {mode === Mode.Editor3D && (
          <HStack gap={0} w="100%">
            <Text mr={9}>背景色</Text>
            <Spacer />
            <Popover.Root>
              <Popover.Trigger asChild>
                <Button
                  variant="outline"
                  maxWidth={6}
                  minWidth={6}
                  width={6}
                  height={5}
                  p={0}
                  backgroundColor={backgroundColor}
                  borderColor="secondary.700"
                  _hover={{ bg: backgroundColor }}
                  _focus={{ bg: backgroundColor }}
                  _active={{ bg: backgroundColor }}
                />
              </Popover.Trigger>
              <Portal>
                <Popover.Positioner>
                  <Popover.Content w="auto">
                    <Chrome
                      inputType={ChromeInputType.HEXA}
                      showAlpha={false}
                      showEyeDropper={false}
                      color={backgroundColor}
                      onChange={(color) => dispatch(setBackgroundColor(color.hex))}
                    />
                  </Popover.Content>
                </Popover.Positioner>
              </Portal>
            </Popover.Root>
            <IconButton
              aria-label="reset"
              fontSize="sm"
              onClick={(e) => {
                e.stopPropagation()
                dispatch(resetBackgroundColor())
              }}
              size="xs"
              variant="toolbarIcon"
            >
              <ResetIcon />
            </IconButton>
          </HStack>
        )}

        {/* Label font size */}
        <HStack gap={0} w="100%">
          {!isManualEditingLabelFontSize && <Text mr={2}>ラベルサイズ</Text>}
          <Spacer />
          {labelFontSize !== undefined && (
            <RangeInput
              colorPalette="black"
              size="sm"
              key="labelFontSize"
              testId="labelFontSize"
              min={1}
              max={5}
              step={0.1}
              precision={1}
              value={labelFontSize}
              defaultValue={EDITOR_DEFAULT_LABEL_FONT_SIZE}
              displayValue={`${labelFontSize}`}
              onManualEdit={setIsManualEditingLabelFontSize}
              updateValue={(newValue) => {
                dispatch(setLabelFontSize(newValue))
              }}
              onReset={() => {
                setTimeout(() => dispatch(resetLabelFontSize()))
              }}
            />
          )}
        </HStack>

        {/* PCD visibility toggle */}
        <LayerItem
          invisible={isPointCloudInvisible}
          label="表示・非表示"
          updateVisibility={(visibility) => dispatch(setIsPointCloudInvisible(!visibility))}
        />
      </Box>

      {/* Mesh Controls Group */}
      <>
        <Text fontWeight="bold" fontSize="sm" color="gray2.500" mt={3} mb={1}>
          3Dメッシュ
        </Text>

        <Box pl={2}>
          {/* Mesh opacity */}
          <Tooltip
            label={!isMeshFilePresent ? meshUnavailableMessage : undefined}
            showArrow
            positioning={{ placement: 'top' }}
            openDelay={300}
            /* TODO: style migration needed - w=100% */
            /* TODO: style migration needed - shouldWrapChildren=false */
          >
            <Box opacity={isMeshFilePresent ? 1 : 0.6} cursor={isMeshFilePresent ? 'default' : 'not-allowed'}>
              <HStack gap={0} w="100%">
                {!isManualEditingMeshOpacity && <Text mr={2}>不透明度</Text>}
                <Spacer />
                <RangeInput
                  colorPalette="black"
                  size="sm"
                  key="meshOpacity"
                  testId="meshOpacity"
                  min={0}
                  max={100}
                  step={10}
                  precision={0}
                  value={solidMeshTransparency * 100}
                  defaultValue={EDITOR_DEFAULT_MESH_TRANSPARENCY * 100}
                  displayValue={`${solidMeshTransparency * 100}`}
                  unit="%"
                  onManualEdit={setIsManualEditingMeshOpacity}
                  updateValue={(newValue) => {
                    dispatch(setTransparency(newValue / 100))
                  }}
                  onReset={() => {
                    setTimeout(() => dispatch(resetTransparency()))
                  }}
                  disabled={!isMeshFilePresent}
                />
              </HStack>

              {/* Mesh visibility toggle */}
              <LayerItem
                invisible={!isMeshFileVisible}
                label="表示・非表示"
                updateVisibility={(visible) => {
                  if (!isMeshFilePresent) return
                  dispatch(setIsVisible(visible))
                }}
                disabled={!isMeshFilePresent}
              />
            </Box>
          </Tooltip>
        </Box>
      </>
    </>
  )
}

export default PCDSettings
